import { useRoute } from "vue-router";
import { defineStore } from 'pinia'
import { v4 as uuid } from 'uuid'
import axios from 'axios'
import domToImage from 'dom-to-image'

import getEditorExtensions from "../utils/tiptap-extensions";
import { useBuilderStyles, DOCUMENT_PDF } from './builder-style.store'
import { extractContent, fixContentFormat } from '../utils/helpers'
import { downloadPdf } from '../api/download'
import { downloadResume as downloadResumeDoc } from '../utils/download-resume-docx'
import { setAuthState } from '../composables/auth'
import { generateJSON } from "@tiptap/core"

import { useResumeScorerStore } from './scorer.store.js';
import { useOnboardingChecklist } from './onboarding-checklist.store.js';
import { usePrimaryResumeStore } from "./primary-resume.store";

import { 
  createResume,
  updateTemplate,
  viewResume,
  retrieveResume,
  updatePreview,
  downloadResume as markResumeDownloaded
} from '../api/builder'

import { 
  createCoverLetter,
  previewCoverLetter,
  retrieveCoverLetter,
  updatePreview as updatePreviewCoverLetter,
  downloadCoverLetter as markCoverLetterDownloaded
} from '../api/cover-letters'

import {
    BuilderSections,
    
    defaultData,
    getName,
    timer,
    parseBuilderDate,
    parseRetrievedResume,
    getFormattedContact,
    getPointsFrom
} from './builder-utils'

const editorInitConfig = {
    isDocumentEditable: true,
    showPersonalInformation: true,
    isDocumentShared: false
}

export const useBuilder = defineStore('pdf-builder', {
  state: () => ({
    _request: null,
    activeRoute: null,

    user: null,

    loading: false,
    saved: false,
    dragging: false,
    isPreview: false,
    isDocumentEditable: true,
    showPersonalInformation: true,
    isDocumentShared: false,

    id: undefined,
    data: null,

    history: [],
    historyIndex: -1,

    _historyTimeout: null,
    isCoverLetter: false
  }),

  actions: {
    async init(id, navigate, activeRoute) {
      this.isDocumentEditable = editorInitConfig.isDocumentEditable;
      this.showPersonalInformation = editorInitConfig.showPersonalInformation;
      this.isDocumentShared = editorInitConfig.isDocumentShared;
    
      this.history = []
      this.historyIndex = -1
      this.data = null
        
      const route = useRoute();

      this.activeRoute = activeRoute

      const coverLetter = this?.activeRoute?.meta?.coverLetter || false;
      this.isCoverLetter = coverLetter;

      try {
        this.loading = true
        this.user = setAuthState().getUser()

        const isNew = !id

        if (isNew) {
          const data = this.buildData({
            // build sections with default values
            ...defaultData(coverLetter, localStorage.getItem('defaultTemplate', 'basic')),
            name: `<b>${getName(this.user)}</b>` || '',
            contact: getFormattedContact(this.user)
          })

          const response = coverLetter ? await createCoverLetter(data) : await createResume(data)
            
          if(route?.query.set_primary === 'true' && !coverLetter) {
            await usePrimaryResumeStore().setPrimaryResume(response.uuid, true)
          }
          
          navigate({ path: coverLetter ? `/cover-letter/${response.uuid}` : `/resume/${response.uuid}`, query: { ...route?.query }})
            
          const parsed = coverLetter ? response : parseRetrievedResume(response)

           // apply styles from parsed resume
           const { line_height, text_size, left_margin, right_margin, font_family, top_margin, bottom_margin  } = parsed['options'] || []
           useBuilderStyles().applyStyles({ font_family, line_height, text_size, left_margin, right_margin, top_margin, bottom_margin });
 
          // apply data 
          this.data = parsed;
          this.id = response.uuid
        } else {
          this.id = id;
          
          const response = coverLetter ? (this.isPreview ? await previewCoverLetter(id) : await retrieveCoverLetter(id)) : (this.isPreview ? await viewResume(id) : await retrieveResume(id))
          const parsed = coverLetter ? response : parseRetrievedResume(response)

          // apply styles from parsed resume
          const { font_family, line_height, text_size, left_margin, right_margin, top_margin, bottom_margin  } = parsed['options'] || []
          useBuilderStyles().applyStyles({ font_family, line_height, text_size, left_margin, right_margin, top_margin, bottom_margin });

          // apply data 
          this.data = parsed;
        }

        this.user = setAuthState().getUser()

        this.saveHistory()
      } finally {
        this.loading = false
      }
    },

    initUsingObject(resumeObject, config = editorInitConfig) {
      const { isDocumentEditable, showPersonalInformation, isDocumentShared } = config;
      
      this.isDocumentEditable = isDocumentEditable;
      this.showPersonalInformation = showPersonalInformation;
      this.isDocumentShared = isDocumentShared;

      this.loading = true

      try {
          const parsed = parseRetrievedResume(resumeObject)

          // apply styles from parsed resume
          const {font_family, line_height, text_size, left_margin, right_margin} = parsed['options'] || []
          useBuilderStyles().applyStyles({font_family, line_height, text_size, left_margin, right_margin});

          // apply data
          this.data = parsed;
          
          this.user = setAuthState().getUser();
          this.saveHistory();
      } finally {
          this.loading = false
      }
    },

    resetState(){
        this._request = null
        this.activeRoute = null

        this.user = null

        this.loading = false
        this.saved = false
        this.dragging = false
        this.isPreview = false
        this.isDocumentEditable = true;
        this.showPersonalInformation = true;

        this.id = undefined
        this.data = null

        this.history = []
        this.historyIndex = -1

        this._historyTimeout = null
        this.isCoverLetter = false
    },

    cancelRequest() {
      this._request?.cancel('Canceled')
    },

    handleDragStart(e){
      this.dragging = true;
    },
      
    handleDragEnd({newIndex, oldIndex}){
      this.dragging = false;

      if( newIndex !== oldIndex ) {
          this.reorder();
      }
    },

    changeTitle(value) {
        if(this.data?.title == null || this.data?.title === value.value) return
        this.data.title = value || 'Untitled'
        this.save(true)
    },

    changeName(name) {
      this.data.name = ('' + name).trim()
      this.save()
    },

    changeContact(contact) {
      this.data.contact = ('' + contact).trim()
      this.save()
    },

    changeMessage(message) {
      this.data.message = ('' + message).trim()
      this.save()
    },
    
    updateMessage(editor, message) {
        editor.commands.setContent({
            type: 'doc', content: [{
                type: 'paragraph', content: [{
                    type: 'text', text: message,
                },],
            },],
        })

        this.save()
    },
    updateSection(editor, message) {
       const selection = editor.view.state.selection;
         
        editor.chain().focus().insertContentAt({
            from: selection.from,
            to: selection.to
        }, message).run();

        this.save()
    },

    changeTemplate(newTemplate) {
      updateTemplate(this.id, newTemplate)
        .then(() => {
          this.data.template = newTemplate
        })
    },

    // working with section && items
    changeSectionName(sectionId, sectionType, value) {
      const { meta: { coverLetter = false } } = this.activeRoute

      this.data.sections.find(e => e.id === sectionId)
        .title = ('' + value).trim() || defaultData(coverLetter, this.data.template).sections.find(e => e.type === sectionType).title

      this.save()
    },
    
    getSectionById(sectionId) {
     return this.data.sections.find(e => e.id === sectionId)
    },
    
    changeSectionKey(sectionId, sectionType, key, value, uuid) {
      const targetSection = this.data.sections.find(e => e.id === sectionId)
      if(targetSection == null) return

      const { meta: { coverLetter = false } } = this.activeRoute
      
      // identify item by ID, find index of item
      const itemIndex = targetSection.items.findIndex(item => item.uuid == uuid)

      // we're using section type to get default values
      let formattedValue = typeof value === 'boolean' ? value : (('' + value).trim() || defaultData(coverLetter, this.data.template).sections.find(e => e.type === sectionType).items[itemIndex][key])

      if(targetSection != null){
        targetSection.items[itemIndex][key] = formattedValue
      }

      this.save()
    },

    changeSectionDate(sectionId, sectionType, key, {date: value, only_year}, uuid) {
      const targetSection = this.data.sections.find(e => e.id === sectionId)
      if(targetSection == null) return

      const { meta: { coverLetter = false } } = this.activeRoute

      const itemIndex = targetSection.items.findIndex(item => item.uuid == uuid)

      let formattedValue = typeof value === 'boolean' ? value : (('' + value).trim() || defaultData(coverLetter, this.data.template).sections.find(e => e.type === sectionType).items[itemIndex][key])

      targetSection.items[itemIndex][key] = formattedValue
      targetSection.items[itemIndex]['only_year'] = only_year

      this.save()
    },

    changeSectionDateRange(sectionId, {date: value, only_year}, uuid, key) {
      const targetSection = this.data.sections.find(e => e.id === sectionId)
      if(targetSection == null) return

      const itemIndex = targetSection.items.findIndex(item => item.uuid == uuid)

      targetSection.items[itemIndex][key] = value || (dateIndex === 'end_at' ? 'Finish Date' : 'Start Date')
      targetSection.items[itemIndex]['only_year'] = only_year

      this.save()
    },

    changeExperience(uuid, val, sectionId, keyToSave = 'bullet_points') {
      const targetSection = this.data.sections.find(e => e.id === sectionId)
      if(targetSection == null) return
      
      // identify item by ID, find index of item
      const itemIndex = targetSection.items.findIndex(item => item.uuid == uuid)

      targetSection.items[itemIndex][keyToSave] = val

      this.save()
    },

    removeSection(sectionId) {
      this.data.sections = this.data.sections
        .filter(({ id }) => id !== sectionId)

      this.save()
    },

    addSection(sectionType, defaultValues = null) {
      const { meta: { coverLetter = false } } = this.activeRoute

      this.data.sections.push({
        ...defaultData(coverLetter, this.data.template).sections.find(({ type }) => type === sectionType), ...defaultValues, ...{id: Date.now()}
      })

      this.save(false, 10, true)
    },

    removeSectionItem(sectionId, uuid) {
      const targetSection = this.data.sections.find(e => e.id === sectionId)
      if(targetSection == null) return

      const itemIndex = targetSection.items.findIndex(item => item.uuid == uuid)

      let el = targetSection.items[itemIndex] ?? null;
      if( !el) return

      let els = targetSection.items;

      // Find index of items with id equal to idToDelete
      const indexesToDelete = els.reduce((acc, item, index) => {
        if (item.parent_uuid === el.uuid) {
            acc.push(index);
        }
        return acc;
      }, []);

      indexesToDelete.unshift(itemIndex)

      // Delete items from the array using splice
      indexesToDelete.reverse().forEach(index => {
        targetSection.items.splice(itemIndex, 1);
      });

      this.save()
    },

    async addSectionItem(sectionId, sectionType, defaultParams = {}) {
      const { meta: { coverLetter = false } } = this.activeRoute

      const defaultValue = defaultData(coverLetter, this.data.template)
        .sections
        .find(({ type }) => type === sectionType)
        ?.items?.[0] ?? null

      let totalItems = this.data.sections
        .find(({ id }) => id === sectionId)
        ?.items?.length || 0

      let orderItem = totalItems + 1;

      this.data.sections
          .find(({ id }) => id === sectionId)
          ?.items.push({...defaultValue , ...defaultParams, ...{id: Date.now(), uuid: uuid(), order: orderItem} })

      this.save(false, 1500, false)
    },

    reorder() {
      this.data.sections = this.data.sections.map((section, index) => ({
        ...section,
        order: index,
      }))

      this.save(false, 1500)
    },

    reorderItems(sectionIndex, items) {
      this.data.sections[sectionIndex].items = items
      this.save(false, 1500)
    },

    saveHistory() {
      const { fontFamily, textSize, lineHeight, leftMargin, rightMargin } = useBuilderStyles()

      const data = JSON.parse(JSON.stringify({
        ...this.data,
        options: {
          font_family: fontFamily,
          text_size: textSize,
          line_height: lineHeight,
          left_margin: leftMargin, 
          right_margin: rightMargin,
        }
      }))

      this.history = this.history.slice(0, this.historyIndex + 1)
      this.history.push(data)
      this.historyIndex = this.historyIndex + 1
    },

    undo() {
      if (this.historyIndex <= 0) {
        return
      }

      this.historyIndex -= 1
      const data = this.history[this.historyIndex]

      this.afterHistoryAction(data)
    },

    redo() {
      if (this.historyIndex === this.history.length - 1) {
        return
      }

      this.historyIndex += 1
      const data = this.history[this.historyIndex]

      this.afterHistoryAction(data)
    },

    afterHistoryAction(data) {
      this.data = JSON.parse(JSON.stringify(data))
      const { font_family, line_height, text_size, left_margin, right_margin, top_margin, bottom_margin } =  this.data['options'] || []
      useBuilderStyles().applyStyles({ font_family, text_size, line_height, left_margin, right_margin, top_margin, bottom_margin });

      this.updateAfterHistory()
    },

    updateAfterHistory() {
      clearTimeout(this._historyTimeout)
      this._historyTimeout = setTimeout(() => {
        this.save(true)
      }, 3000)
    },

    buildData(customData) {
      const data = customData || this.data
      const { textSize, lineHeight, leftMargin, rightMargin, topMargin, bottomMargin, fontFamily } = useBuilderStyles()

      const coverLetter = this?.activeRoute?.meta?.coverLetter || false;
      const resumeScorer = useResumeScorerStore();

      const builderData = {
        ...data,
        id: this.id,
        template: localStorage.getItem('defaultTemplate', 'basic'),
        options: {
            text_size: textSize,
            line_height: lineHeight,
            left_margin: leftMargin,
            top_margin: topMargin,
            bottom_margin: bottomMargin,
            right_margin: rightMargin,
            font_family: fontFamily
        },
        meta: { 'resume-score': customData?.score || resumeScorer.totalScore }
      }

      // if not cover letter adding section key
      if(! coverLetter) {
        builderData.sections = [
          ...data.sections.map((section, index) => ({
            ...section,
            order: index,
            ...(
              (section.type === BuilderSections.CustomExperience) ? {} : {
                items: section.items.map(item => ({
                  ...item,
                  ...(section.type === BuilderSections.Education ? {
                    ...parseBuilderDate('date', item),
                  } : {
                    ...parseBuilderDate('start_at', item),
                    ...parseBuilderDate('end_at', item, true),
                  })
                }))
              }
            ),
          }))
        ]
      }

      return builderData
    },

    save(ignoreHistory = false, delay = 10, updateState = false, onSucess = null, onError = null) {
      if (!ignoreHistory) {
        this.saveHistory()
      }

      this._request?.cancel('Canceled')

      const { meta: { coverLetter = false } } = this.activeRoute

      setTimeout(() => {
        this._request = axios.CancelToken.source()

        const data = this.buildData()

        axios.put(coverLetter ? `/api/cover-letters/${this.id}` : `/api/resumes/${this.id}`, data, {
          cancelToken: this._request.token,
        })
          .then(async ({data: { data: newData }}) => {
            if(updateState) {
              this.data = parseRetrievedResume(newData)
            }

            // call callable function
            if(onSucess) onSucess(newData)

            this.savePreview()
            this.saved = true
            
            await timer(2000)
            this.saved = false
          })
          .catch(err => {
            if(onError) onError(err)
          });
      }, delay)
    },

   saveResumeScore() {
      const route = useRoute();

      if(!setAuthState().isAuthenticated() || route.name !== 'resumes.edit') return;

      const resumeScorer = useResumeScorerStore();

      axios.post(`/api/resumes/${this.id}/resume-score`, {
          resumeScore: resumeScorer.totalScore
      }).catch(() => {});
   },
      
    async savePreview() {
      const { meta: { coverLetter = false } } = this.activeRoute

      const filter = (node) => node.tagName?.toLowerCase() !== 'img'

      const element = document.getElementById('pdf-resume-builder-content')

      if(element instanceof HTMLElement){
        const blob = await domToImage.toBlob(element, {
          filter,
          quality: 1,
        })
  
        coverLetter ? updatePreviewCoverLetter(this.id, blob) : updatePreview(this.id, blob)
      }
    },

    async download(id, format = DOCUMENT_PDF, isCoverLetter) {
      const coverLetter = this?.activeRoute?.meta?.coverLetter || isCoverLetter || false;

      coverLetter 
        ? markCoverLetterDownloaded(id || this.id)
        : markResumeDownloaded(id || this.id)

      const builderStyle = useBuilderStyles()
      builderStyle.setCurrentDocument(format)

      const { generatorDefaultSize: defaultSize, generatorDefaultSizeCoverLetter, generatorHeadingSize, generatorLineHeight, generatorMargins, generatorFontFamily } = builderStyle
      const generatorDefaultSize = coverLetter ? generatorDefaultSizeCoverLetter : defaultSize

      const resume = coverLetter ? await retrieveCoverLetter(id || this.id) : await retrieveResume(id || this.id)
      const data = coverLetter ? resume :  parseRetrievedResume(resume)

        if (format == DOCUMENT_PDF) {
          const parsedURL = new URL(window.location.href)
          const host = parsedURL.hostname === 'localhost' ? 'http://localhost:8000' : parsedURL.origin
          const url = `${host}/${coverLetter ? 'cover-letter' : 'resume'}/preview/${id || this.id}`

          if (this?.user == null) {
              this.user = setAuthState().getUser();
          }

          await downloadPdf({url, user: {name: this.user?.name, email: this.user?.email}}, extractContent(data.title))
        } else {
        downloadResumeDoc(data, {
          generatorDefaultSize, generatorHeadingSize, generatorLineHeight, generatorMargins, generatorFontFamily, isCoverLetter: coverLetter
        })
      }
      
      const onboardingChecklistStore = useOnboardingChecklist()
      
      if(coverLetter) {
        onboardingChecklistStore.markItemAsCompleted(2, 'downloadCoverLetter')
      } else {
        onboardingChecklistStore.markItemAsCompleted(1, 'downloadResume')
      }
    },
  },

  getters: {
    bulletPoints() {
        return this.bulletPointsWithId.map(item => item.bullet)
    },
    bulletPointsWithId({ data }) {
      let bulletPoints = []
      const sections = data?.sections || [];
      
      if(sections.length === 0) return []

      sections.forEach(section => {
        const items = section.items;
        
        if (!Array.isArray(items) && items?.length === 0) return

        items.forEach(item => {
          if (typeof item?.bullet_points !== 'string' || item?.bullet_points === '') return

          const jsonContent = generateJSON(
              fixContentFormat(item.bullet_points),
              getEditorExtensions(true, true, 'true', undefined)
          )
		
          jsonContent.content.forEach((item) => {
            if (item.type !== 'bulletList') {
              return
            }
            
						if(Array.isArray(item?.content)){
								item?.content?.forEach((listItem) => {
										bulletPoints.push(getPointsFrom(listItem))
								})
						}else{
								bulletPoints.push(...getPointsFrom(item))
						}
          })
        })
      })

      return bulletPoints;
    },
    loaded({ loading, data }) {
      return !loading && !!data
    },
  }
})
